import type { CSSProperties } from 'react';

import './Skeleton.css';

export type SkeletonProps = {
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
};

export function Skeleton({
  width = '100%',
  height = 20,
  className,
  style,
  ariaLabel,
}: SkeletonProps) {
  return (
    <div
      className={className ? `skeleton ${className}` : 'skeleton'}
      role="status"
      aria-busy="true"
      aria-label={ariaLabel ?? 'Loading'}
      style={{ width, height, ...style }}
    />
  );
}

export function SkeletonCard() {
  return (
    <div className="skeleton-card" data-testid="skeleton-card">
      <Skeleton className="skeleton-card__media" height={224} ariaLabel="Loading image" />
      <div className="skeleton-card__body">
        <Skeleton width="55%" height={10} />
        <Skeleton width="80%" height={20} />
        <Skeleton width="40%" height={14} />
        <div className="skeleton-card__divider">
          <Skeleton width="65%" height={22} />
        </div>
      </div>
    </div>
  );
}

export type SkeletonTextProps = {
  /** Number of stacked lines. */
  lines?: number;
  /** Width of the final line (narrower mimics a paragraph end). */
  lastWidth?: number | string;
  lineHeight?: number | string;
  gap?: number | string;
  className?: string;
};

/**
 * Stacked text-line skeleton for paragraphs / leads / rich-text blocks.
 * Composes the `Skeleton` bar so pulse, colour and radius stay consistent.
 */
export function SkeletonText({
  lines = 3,
  lastWidth = '60%',
  lineHeight = 12,
  gap = 10,
  className,
}: SkeletonTextProps) {
  return (
    <div
      className={className ? `skeleton-text ${className}` : 'skeleton-text'}
      style={{ display: 'flex', flexDirection: 'column', gap }}
    >
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          height={lineHeight}
          width={i === lines - 1 && lines > 1 ? lastWidth : '100%'}
        />
      ))}
    </div>
  );
}
